import type { Question } from '@trivia/shared';

/** Objects, technology, money and food — the scale of ordinary life. */
export const everydayQuestions: Question[] = [
  {
    id: 'everyday.flights-per-day',
    type: 'numeric',
    prompt: 'How many commercial flights take off around the world on a typical day?',
    category: 'Technology',
    difficulty: 2,
    tags: ['aviation'],
    answer: 100_000,
    displayAnswer: 'about 100,000',
    unit: 'flights',
    magnitude: 'thousands',
    tolerance: 0.3,
    reveal: {
      headline: 'about 100,000',
      explanation:
        'Roughly 100,000 commercial flights operate daily, and at any given moment somewhere between 8,000 and 20,000 aircraft are in the air depending on the time of day.',
      comparison: 'About a million people are airborne right now — a flying city the size of Birmingham.',
    },
    source: {
      citation: 'Air traffic and flight-tracking industry figures',
      statisticYear: 2023,
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'everyday.air-passengers',
    type: 'numeric',
    prompt: 'How many passengers flew on commercial airlines worldwide in 2019?',
    category: 'Technology',
    difficulty: 2,
    tags: ['aviation'],
    answer: 4.5e9,
    displayAnswer: 'about 4.5 billion',
    unit: 'passengers',
    magnitude: 'billions',
    tolerance: 0.28,
    reveal: {
      headline: 'about 4.5 billion',
      explanation:
        'That counts journeys, not people — frequent flyers are counted many times over. Estimates suggest only around one in ten people alive has ever flown at all.',
      comparison: 'Enough boarding passes for well over half the planet, issued in a single year.',
    },
    source: {
      citation: 'World Air Transport Statistics',
      publisher: 'IATA',
      statisticYear: 2019,
      kind: 'measured',
    },
  },
  {
    id: 'everyday.internet-users',
    type: 'numeric',
    prompt: 'How many people in the world use the internet?',
    category: 'Technology',
    difficulty: 2,
    tags: ['internet'],
    answer: 5.4e9,
    displayAnswer: 'about 5.4 billion',
    unit: 'people',
    magnitude: 'billions',
    tolerance: 0.22,
    reveal: {
      headline: 'about 5.4 billion',
      explanation:
        'Roughly two thirds of humanity is online. The 2.6 billion still offline are concentrated in low-income countries, and the gap is wider for women than men almost everywhere.',
      comparison: 'In 2000 the figure was about 400 million. It has grown more than thirteenfold since.',
    },
    source: {
      citation: 'Facts and Figures',
      publisher: 'International Telecommunication Union',
      statisticYear: 2023,
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'everyday.smartphones-sold',
    type: 'numeric',
    prompt: 'How many smartphones are sold worldwide in a year?',
    category: 'Technology',
    difficulty: 2,
    tags: ['devices'],
    answer: 1.2e9,
    displayAnswer: 'about 1.2 billion',
    unit: 'phones',
    magnitude: 'billions',
    tolerance: 0.28,
    reveal: {
      headline: 'about 1.2 billion',
      explanation:
        'Shipments peaked near 1.5 billion in 2017 and have since settled around 1.2 billion as people hold on to handsets for longer.',
      comparison: 'That is roughly 38 phones sold every second, day and night, all year.',
    },
    source: {
      citation: 'Quarterly Mobile Phone Tracker',
      publisher: 'IDC',
      statisticYear: 2023,
      kind: 'measured',
      volatile: true,
    },
  },
  {
    id: 'everyday.cars-in-world',
    type: 'numeric',
    prompt: 'How many motor vehicles are in use around the world?',
    category: 'Technology',
    difficulty: 2,
    tags: ['transport'],
    answer: 1.5e9,
    displayAnswer: 'about 1.5 billion',
    unit: 'vehicles',
    magnitude: 'billions',
    tolerance: 0.28,
    reveal: {
      headline: 'about 1.5 billion',
      explanation:
        'Cars, vans, trucks and buses together number around 1.5 billion. The global fleet has roughly doubled since 2000.',
      comparison: 'About one vehicle for every five people alive — and most of them are parked 95% of the time.',
    },
    source: {
      citation: 'International vehicle fleet statistics',
      publisher: 'OICA',
      statisticYear: 2023,
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'everyday.mcdonalds',
    type: 'numeric',
    prompt: 'How many McDonald’s restaurants are there worldwide?',
    category: 'Food',
    difficulty: 2,
    tags: ['brands'],
    answer: 42_000,
    displayAnswer: 'about 42,000',
    unit: 'restaurants',
    magnitude: 'thousands',
    tolerance: 0.28,
    reveal: {
      headline: 'about 42,000',
      explanation:
        'Around 42,000 restaurants operate in over 100 countries, serving on the order of 70 million people a day.',
      comparison: 'That is roughly one McDonald’s for every 190,000 people on Earth.',
    },
    source: {
      citation: 'Annual Report',
      publisher: 'McDonald’s Corporation',
      statisticYear: 2023,
      kind: 'measured',
      volatile: true,
    },
  },
  {
    id: 'everyday.rice-grains',
    type: 'numeric',
    prompt: 'Roughly how many grains of rice are in a one kilogram bag?',
    subPrompt: 'Assume ordinary long-grain white rice.',
    category: 'Food',
    difficulty: 2,
    tags: ['estimation'],
    answer: 50_000,
    displayAnswer: 'about 50,000',
    unit: 'grains',
    magnitude: 'thousands',
    tolerance: 0.26,
    reveal: {
      headline: 'about 50,000',
      explanation:
        'A single grain of long-grain white rice weighs roughly 20 milligrams, so a kilogram holds about 50,000 of them.',
      comparison: 'Counting them one per second would take you around fourteen hours.',
    },
    source: {
      citation: 'Derived from standard grain mass',
      kind: 'estimated',
      assumptions: ['About 20 mg per grain; short-grain varieties run heavier.'],
    },
  },
  {
    id: 'everyday.gold-mined',
    type: 'numeric',
    prompt: 'How much gold has been mined in all of human history?',
    category: 'Money',
    difficulty: 3,
    tags: ['materials'],
    answer: 210_000,
    displayAnswer: 'about 210,000 tonnes',
    unit: 'tonnes',
    magnitude: 'thousands',
    tolerance: 0.3,
    reveal: {
      headline: 'about 210,000 tonnes',
      explanation:
        'Every gram ever mined, melted together, would form a cube roughly 22 metres on each side. Around two thirds of it has been extracted since 1950.',
      comparison: 'All the gold in history would fit comfortably inside a modest suburban house.',
    },
    source: {
      citation: 'Gold Demand Trends',
      publisher: 'World Gold Council',
      statisticYear: 2023,
      kind: 'estimated',
      volatile: true,
    },
  },
  {
    id: 'everyday.empire-state-bricks',
    type: 'numeric',
    prompt: 'How many bricks were used to build the Empire State Building?',
    category: 'Everyday',
    difficulty: 3,
    tags: ['buildings'],
    answer: 10_000_000,
    displayAnswer: 'about 10 million',
    unit: 'bricks',
    magnitude: 'millions',
    tolerance: 0.34,
    reveal: {
      headline: 'about 10 million',
      explanation:
        'The building went up in 410 days during the Great Depression, at an average pace of more than four floors a week.',
      comparison: 'That is roughly 24,000 bricks laid every single day of construction.',
    },
    source: {
      citation: 'Empire State Building construction records',
      kind: 'estimated',
    },
  },
  {
    id: 'everyday.eiffel-mass',
    type: 'numeric',
    prompt: 'How much does the iron structure of the Eiffel Tower weigh?',
    category: 'Everyday',
    difficulty: 3,
    tags: ['buildings'],
    answer: 7300,
    displayAnswer: 'about 7,300 tonnes',
    unit: 'tonnes',
    magnitude: 'thousands',
    tolerance: 0.3,
    reveal: {
      headline: 'about 7,300 tonnes',
      explanation:
        'The ironwork weighs about 7,300 tonnes; with foundations and fittings the whole tower is around 10,100 tonnes. It is mostly empty space held up by lattice.',
      comparison: 'If you melted the iron down into a sheet covering its square base, the metal would be only about 6 cm deep.',
    },
    source: {
      citation: 'Official Eiffel Tower technical data',
      publisher: 'Societe d’Exploitation de la Tour Eiffel',
      kind: 'measured',
    },
  },
  {
    id: 'everyday.blue-whale-heart',
    type: 'numeric',
    prompt: 'How much does the heart of a blue whale weigh?',
    category: 'Animals',
    difficulty: 2,
    tags: ['whales'],
    answer: 180,
    displayAnswer: 'about 180 kg',
    unit: 'kg',
    magnitude: 'small',
    tolerance: 0.3,
    reveal: {
      headline: 'about 180 kg',
      explanation:
        'The only blue whale heart ever preserved, at the Royal Ontario Museum, weighed about 180 kg. Popular claims that it is the size of a small car are a considerable exaggeration.',
      comparison: 'It beats as slowly as twice a minute when the whale dives, and can be heard from three kilometres away.',
    },
    source: {
      citation: 'Royal Ontario Museum blue whale specimen',
      publisher: 'Royal Ontario Museum',
      kind: 'measured',
      assumptions: ['Based on a single preserved specimen; individual whales vary.'],
    },
  },
  {
    id: 'everyday.rubiks-cube',
    type: 'numeric',
    prompt: 'How many different arrangements does a standard 3x3 Rubik’s Cube have?',
    category: 'Logic',
    difficulty: 4,
    tags: ['combinatorics', 'wildcard'],
    answer: 4.325e19,
    displayAnswer: 'about 43 quintillion',
    unit: 'arrangements',
    magnitude: 'astronomical',
    tolerance: 0.5,
    reveal: {
      headline: '43,252,003,274,489,856,000',
      explanation:
        'About 43 quintillion arrangements — and yet every single one of them can be solved in 20 moves or fewer. That upper bound, proved in 2010, is known as God’s Number.',
      comparison: 'If you had turned one cube every second since the Big Bang, you would still have barely started.',
    },
    source: {
      citation: 'Rokicki et al., "God’s Number is 20"',
      year: 2010,
      kind: 'definitional',
    },
  },
  {
    id: 'everyday.chess-games',
    type: 'numeric',
    prompt: 'Roughly how many possible games of chess are there?',
    category: 'Logic',
    difficulty: 5,
    tags: ['combinatorics', 'wildcard'],
    answer: 1e120,
    displayAnswer: 'around 10 to the power of 120',
    unit: 'games',
    magnitude: 'astronomical',
    tolerance: 12,
    reveal: {
      headline: '10^120',
      explanation:
        'Claude Shannon estimated the number of possible chess games at around 10 to the 120th in 1950. It is now known as the Shannon number, and it is not a count so much as a demonstration that chess cannot be brute-forced.',
      comparison: 'There are only about 10^80 atoms in the observable universe. Chess has more games than the universe has atoms — by forty orders of magnitude.',
    },
    source: {
      citation: 'Shannon, "Programming a Computer for Playing Chess"',
      year: 1950,
      kind: 'estimated',
      assumptions: ['A rough lower bound on game-tree size, not an exact enumeration.'],
    },
  },
  {
    id: 'everyday.deck-vs-atoms',
    type: 'which-is-bigger',
    prompt: 'Which number is larger?',
    category: 'Logic',
    difficulty: 4,
    tags: ['combinatorics', 'wildcard'],
    options: [
      { label: 'Ways to shuffle a deck of 52 cards', detail: 'about 8 x 10^67' },
      { label: 'Atoms in the observable universe', detail: 'about 10^80' },
    ],
    answer: 1,
    reveal: {
      headline: 'Atoms, by a long way',
      explanation:
        'A shuffled deck has about 8 x 10^67 orderings — an absurd number, but the observable universe still holds roughly a trillion times a trillion times more atoms than that.',
      comparison: 'Even so: properly shuffle a deck and you have almost certainly created an order that has never existed before and never will again.',
    },
    source: {
      citation: '52 factorial; standard cosmological atom-count estimates',
      kind: 'estimated',
    },
  },
];
